var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { krBaseCSS } from '../../style/base.js';
/**
 * A select dropdown component that works with native browser forms.
 *
 * Uses ElementInternals for form association, allowing the component
 * to participate in form submission, validation, and reset.
 *
 * @element kr-select-field
 * @slot - The kr-select-option elements
 */
export class KRSelectField extends LitElement {
    constructor() {
        super();
        // The value the user last picked. While dirty, an external `value` set is reverted to this (see
        // willUpdate) so a parent re-binding from the server can't overwrite an unsaved edit.
        this._dirtyValue = '';
        /**
         * The select label text
         */
        this.label = '';
        /**
         * The input name for form submission
         */
        this.name = '';
        /**
         * The currently selected value
         */
        this.value = '';
        /**
         * Placeholder text when no option is selected
         */
        this.placeholder = 'Select an option';
        /**
         * Whether the select is disabled
         */
        this.disabled = false;
        /**
         * Whether the field is required
         */
        this.required = false;
        /**
         * Whether the field is readonly
         */
        this.readonly = false;
        /**
         * Helper text shown below the select
         */
        this.hint = '';
        /**
         * Whether the user has changed this field since it was last loaded or marked pristine. Set when
         * the user picks an option, cleared by markPristine(). Used by <kr-form> to collect changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's edit is in flight. Set by markSaving(), cleared
         * by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._isOpen = false;
        this._highlightedIndex = -1;
        this._touched = false;
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._handleOutsideClick = (e) => {
            if (!e.composedPath().includes(this)) {
                this._close();
            }
        };
        this._handleKeyDown = (e) => {
            if (!this._isOpen) {
                return;
            }
            const options = Array.from(this.querySelectorAll('kr-select-option'));
            switch (e.key) {
                case 'Escape':
                    this._close();
                    this._triggerElement?.focus();
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    if (options.some(o => !o.disabled)) {
                        let newIndex = this._highlightedIndex + 1;
                        while (newIndex < options.length && options[newIndex]?.disabled) {
                            newIndex++;
                        }
                        if (newIndex < options.length) {
                            this._highlightedIndex = newIndex;
                        }
                    }
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    {
                        let newIndex = this._highlightedIndex - 1;
                        while (newIndex >= 0 && options[newIndex]?.disabled) {
                            newIndex--;
                        }
                        if (newIndex >= 0) {
                            this._highlightedIndex = newIndex;
                        }
                    }
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (this._highlightedIndex >= 0 && this._highlightedIndex < options.length) {
                        this._selectOption(options[this._highlightedIndex]);
                    }
                    break;
            }
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        document.addEventListener('click', this._handleOutsideClick);
        document.addEventListener('keydown', this._handleKeyDown);
        this.addEventListener('invalid', this._handleInvalid);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._handleOutsideClick);
        document.removeEventListener('keydown', this._handleKeyDown);
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._updateValidity();
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved edit, an external `value` set (e.g. a parent re-binding from a
        // server response that predates the edit) would overwrite it. Revert to the user's value. The
        // field accepts external values again once the edit is saved (markSaving clears dirty) or reset.
        if (changedProperties.has('value') && this.dirty && this.value !== this._dirtyValue && saveMode === 'autosave') {
            this.value = this._dirtyValue;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('required') || changedProperties.has('value')) {
            this._updateValidity();
        }
    }
    // Form-associated custom element callbacks
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    get willValidate() {
        return this._internals.willValidate;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        this.value = '';
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._internals.setFormValue('');
        this._internals.setValidity({});
    }
    formStateRestoreCallback(state) {
        this.value = state;
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's edit starts. Clearing dirty here means a
    // further edit during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user edited mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the edit.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyValue = this.value;
    }
    focus() {
        this._triggerElement?.focus();
    }
    blur() {
        this._triggerElement?.blur();
    }
    _handleTriggerClick() {
        if (this.disabled || this.readonly) {
            return;
        }
        if (this._isOpen) {
            this._close();
        }
        else {
            this._isOpen = true;
            const options = Array.from(this.querySelectorAll('kr-select-option'));
            this._highlightedIndex = options.findIndex(o => o.value === this.value);
            // Position the fixed dropdown relative to the trigger
            requestAnimationFrame(() => {
                const dropdown = this.shadowRoot?.querySelector('.select-dropdown');
                if (dropdown) {
                    const triggerRect = this._triggerElement.getBoundingClientRect();
                    const spaceBelow = window.innerHeight - triggerRect.bottom - 4 - 8;
                    dropdown.style.top = triggerRect.bottom + 4 + 'px';
                    dropdown.style.left = triggerRect.left + 'px';
                    // Match the trigger width as a MINIMUM, but let the dropdown grow to fit
                    // longer option text instead of clipping it (e.g. a narrow "All" trigger
                    // must still show "Archived" in full).
                    dropdown.style.minWidth = triggerRect.width + 'px';
                    dropdown.style.maxHeight = spaceBelow + 'px';
                }
            });
        }
    }
    _close() {
        this._isOpen = false;
        this._highlightedIndex = -1;
    }
    _selectOption(option) {
        if (option.disabled) {
            return;
        }
        this.value = option.value;
        this.dirty = true;
        this._dirtyValue = this.value;
        this._internals.setFormValue(this.value);
        this._updateValidity();
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true
        }));
        this._close();
        this._triggerElement?.focus();
    }
    _handleBlur() {
        this._touched = true;
        this._updateValidity();
    }
    _handleOptionMouseEnter(idx) {
        this._highlightedIndex = idx;
    }
    _handleSlotChange() {
        this.requestUpdate();
    }
    _updateValidity() {
        if (this.required && !this.value) {
            this._internals.setValidity({ valueMissing: true }, 'Please select an option', this._triggerElement);
        }
        else {
            this._internals.setValidity({});
        }
    }
    render() {
        const options = Array.from(this.querySelectorAll('kr-select-option'));
        const selectedLabel = options.find(o => o.value === this.value)?.label;
        return html `
      <div class="wrapper">
        ${this.label
            ? html `
            <label>
              ${this.label}
              ${this.required ? html `<span class="required" aria-hidden="true">*</span>` : ''}
            </label>
          `
            : nothing}
        <div class="select-wrapper">
          <button
            class=${classMap({
            'select-trigger': true,
            'select-trigger--open': this._isOpen,
            'select-trigger--invalid': this._touched && this.required && !this.value,
        })}
            type="button"
            ?disabled=${this.disabled}
            aria-haspopup="listbox"
            aria-expanded=${this._isOpen}
            @click=${this._handleTriggerClick}
            @blur=${this._handleBlur}
          >
            <span class=${classMap({
            'select-value': true,
            'select-placeholder': !selectedLabel
        })}>
              ${selectedLabel || this.placeholder}
            </span>
            <svg
              class=${classMap({
            'chevron-icon': true,
            'select-icon': true,
            'select-icon--open': this._isOpen
        })}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
            </svg>
          </button>

          <div class=${classMap({
            'select-dropdown': true,
            'hidden': !this._isOpen
        })} role="listbox">
            <div class="select-options">
              ${options.length === 0
            ? html `<div class="select-empty">No options available</div>`
            : options.map((option, idx) => {
                const isSelected = option.value === this.value;
                return html `
                    <div
                      class=${classMap({
                    'select-option': true,
                    'select-option--selected': isSelected,
                    'select-option--disabled': option.disabled,
                    'select-option--highlighted': idx === this._highlightedIndex,
                })}
                      role="option"
                      aria-selected=${isSelected}
                      @click=${() => this._selectOption(option)}
                      @mouseenter=${() => this._handleOptionMouseEnter(idx)}
                    >
                      ${option.label}
                    </div>
                  `;
            })}
            </div>
          </div>
        </div>
        ${this._touched && this.required && !this.value
            ? html `<div class="validation-message">Please select an option</div>`
            : this.hint
                ? html `<div class="hint">${this.hint}</div>`
                : ''}
      </div>
      <div class="options-slot">
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
    `;
    }
}
KRSelectField.styles = [krBaseCSS, css `
    :host {
      display: block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    label {
      font-size: 14px;
      font-weight: 500;
      color: black;
    }

    .required {
      color: var(--kr-select-required-color, #ef4444);
      margin-left: 0.125rem;
    }

    .select-wrapper {
      position: relative;
    }

    .select-trigger {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: var(--kr-select-padding, 10px 12px);
      font-family: inherit;
      font-size: 14px;
      line-height: 1.5;
      color: var(--kr-select-color, #000000);
      background-color: var(--kr-select-bg, #fff);
      border: 1px solid var(--kr-select-border-color, #0000003d);
      border-radius: 8px;
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s;
      text-align: left;
    }

    .select-trigger:hover:not(:disabled) {
      border-color: var(--kr-select-hover-border-color, #9ca3af);
    }

    .select-trigger:focus {
      outline: none;
      border-color: var(--kr-primary);
      box-shadow: 0 0 0 3px rgba(22, 48, 82, 0.1);
    }

    .select-trigger:disabled {
      background-color: var(--kr-select-disabled-bg, #f3f4f6);
      color: var(--kr-select-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    .select-trigger--invalid {
      border-color: var(--kr-select-error-border-color, #ef4444);
    }

    .select-trigger--invalid:focus {
      border-color: var(--kr-select-error-border-color, #ef4444);
      box-shadow: 0 0 0 3px var(--kr-select-error-ring-color, rgba(239, 68, 68, 0.1));
    }

    .select-trigger--open {
      border-color: var(--kr-primary);
      box-shadow: 0 0 0 3px rgba(22, 48, 82, 0.1);
    }

    .select-value {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .select-placeholder {
      color: #9ca3af;
    }

    .select-icon {
      flex-shrink: 0;
      margin-left: 0.5rem;
      color: var(--kr-select-icon-color, #6b7280);
      transition: transform 0.2s;
    }

    .select-icon--open {
      transform: rotate(180deg);
    }

    .select-dropdown {
      position: fixed;
      z-index: 10000;
      background: var(--kr-select-dropdown-bg, white);
      border: 1px solid var(--kr-select-dropdown-border-color, #9ba7b6);
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      padding: 8px 0;
      overflow-y: auto;
    }

    .select-options {
      padding: 0;
    }

    .select-option {
      display: flex;
      align-items: center;
      min-height: 36px;
      padding: 8px 16px;
      font-size: 14px;
      color: var(--kr-select-option-color, #000000);
      cursor: pointer;
      border-radius: 4px;
      transition: background-color 0.15s;
    }

    .select-option:hover:not(.select-option--disabled) {
      background-color: var(--kr-select-option-hover-bg, #f3f4f6);
    }

    .select-option--selected {
      background-color: var(--kr-select-option-selected-bg, rgba(22, 48, 82, 0.08));
      color: var(--kr-select-option-selected-color, var(--kr-primary));
    }

    .select-option--selected:hover {
      background-color: var(--kr-select-option-selected-hover-bg, rgba(22, 48, 82, 0.12));
    }

    .select-option--disabled {
      color: var(--kr-select-option-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    .select-option--highlighted {
      background-color: var(--kr-select-option-hover-bg, #f3f4f6);
    }

    .select-empty {
      padding: 1rem;
      text-align: center;
      color: var(--kr-select-empty-color, #6b7280);
      font-size: 14px;
    }

    .hint {
      font-size: 0.75rem;
      color: var(--kr-select-helper-color, #6b7280);
    }

    .validation-message {
      font-size: 0.75rem;
      color: var(--kr-select-error-color, #ef4444);
    }

    .hidden {
      display: none;
    }

    .chevron-icon {
      width: 20px;
      height: 20px;
    }

    /* Hide the slot - options are rendered in dropdown */
    .options-slot {
      display: none;
    }
  `];
// Enable form association
KRSelectField.formAssociated = true;
__decorate([
    property({ type: String })
], KRSelectField.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRSelectField.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRSelectField.prototype, "value", void 0);
__decorate([
    property({ type: String })
], KRSelectField.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean })
], KRSelectField.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRSelectField.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], KRSelectField.prototype, "readonly", void 0);
__decorate([
    property({ type: String })
], KRSelectField.prototype, "hint", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRSelectField.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRSelectField.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRSelectField.prototype, "saving", void 0);
__decorate([
    state()
], KRSelectField.prototype, "_isOpen", void 0);
__decorate([
    state()
], KRSelectField.prototype, "_highlightedIndex", void 0);
__decorate([
    state()
], KRSelectField.prototype, "_touched", void 0);
__decorate([
    query('.select-trigger')
], KRSelectField.prototype, "_triggerElement", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-select-field')) {
    customElements.define('kr-select-field', KRSelectField);
}
//# sourceMappingURL=select-field.js.map